import { Box } from '@mui/material';
import React, { useEffect, useMemo, useRef } from 'react';
import { EditorContent, EditorContext, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TableKit } from '@tiptap/extension-table';
import { Markdown } from '@tiptap/markdown';
import MarkdownToolbar from './Toolbar';
import getEditorSx from '@/components/TiptapEditor/style';
import markdownTableStyles from '@/components/MarkdownEditor/styles';

const MarkdownEditor = ({ value, setValue, height = '50vh' }) => {
	const containerRef = useRef(null);
	const editor = useEditor({
		content: value,
		contentType: 'markdown',
		extensions: [
			StarterKit.configure({
				link: {
					openOnClick: false,
					autolink: true,
					defaultProtocol: 'https',
				},
			}),
			TableKit.configure({
				table: { resizable: false },
			}),
			Markdown,
		],
		editorProps: {
			// Ctrl/Cmd + click opens a link in a new tab; a plain click still places the cursor
			handleClick(view, pos, event) {
				if (!event.ctrlKey && !event.metaKey) return false;

				// Disallowed links (e.g. javascript:) are rendered with an empty href
				const href = event.target.closest('a')?.getAttribute('href');
				if (!href) return false;

				window.open(href, '_blank', 'noopener,noreferrer');
				return true;
			},
		},
		onUpdate({ editor }) {
			setValue(editor.getMarkdown());
		},
		immediatelyRender: false,
	});

	const providerValue = useMemo(() => ({ editor }), [editor]);

	useEffect(() => {
		if (!editor) return;

		if (value !== editor.getMarkdown()) {
			editor.commands.setContent(value, { contentType: 'markdown', emitUpdate: false });
		}
	}, [value, editor]);

	// CSS can't detect held keys, so flag Ctrl/Cmd on the container to show a hand over links.
	// Set on the container rather than the editor element, whose class ProseMirror manages.
	useEffect(() => {
		const updateCtrlHeld = (e) => {
			containerRef.current?.classList.toggle('ctrl-held', e.ctrlKey || e.metaKey);
		};
		const clearCtrlHeld = () => {
			containerRef.current?.classList.remove('ctrl-held');
		};

		window.addEventListener('keydown', updateCtrlHeld);
		window.addEventListener('keyup', updateCtrlHeld);
		window.addEventListener('blur', clearCtrlHeld);
		return () => {
			window.removeEventListener('keydown', updateCtrlHeld);
			window.removeEventListener('keyup', updateCtrlHeld);
			window.removeEventListener('blur', clearCtrlHeld);
		};
	}, []);

	return (
		<EditorContext.Provider value={providerValue}>
			<Box
				ref={containerRef}
				height={height}
				sx={(theme) => ({
					display: 'flex',
					flexDirection: 'column',
					'& .markdown-editor': {
						flex: 1,
						minHeight: 0,
						display: 'flex',
						flexDirection: 'column',
						// Clip the scrollbar to the rounded corners; the scrollbar sits on the inner .tiptap
						overflow: 'hidden',
						border: '1px solid #ccc',
						borderRadius: '4px',
						backgroundColor: '#fff',
						color: 'rgba(0, 0, 0, 0.87)',
						cursor: 'text',
					},
					'& .markdown-editor .tiptap': {
						flex: 1,
						minHeight: 0,
						overflowY: 'auto',
						padding: '12px 15px',
					},
					...getEditorSx(theme),
					// getEditorSx removes paragraph margins for emails; keep Markdown's paragraph spacing
					'& .tiptap p': {
						marginBlockStart: '0.5em',
						marginBlockEnd: '0.5em',
					},
					// getEditorSx sets header cells to normal weight
					'& .tiptap th': {
						fontWeight: 'bold',
					},
					// Disallowed links (e.g. javascript:) have an empty href and can't be opened
					'&.ctrl-held .tiptap a[href]:not([href=""])': {
						cursor: 'pointer',
					},
					...markdownTableStyles,
				})}
			>
				<MarkdownToolbar />
				<EditorContent
					className='markdown-editor'
					editor={editor}
					onMouseDown={(e) => {
						if (!e.target.closest('.tiptap')) {
							e.preventDefault();
							editor?.chain().focus().run();
						}
					}}
				/>
			</Box>
		</EditorContext.Provider>
	);
};
MarkdownEditor.displayName = 'MarkdownEditor';
export default MarkdownEditor;
